// TransformationCache - Handles caching for transformation results
export class TransformationCache {
  constructor(maxSize = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.cacheStats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalSets: 0,
      totalGets: 0
    };
    this.accessTimes = new Map(); // Track access times for LRU
  }

  // Generate cache key for transformation caching
  generateCacheKey(cumulativeData, auditFormData) {
    const dataHash = this.simpleHash(JSON.stringify(cumulativeData));
    const formHash = this.simpleHash(JSON.stringify(auditFormData?.hierarchy || auditFormData?.data || {}));
    return `transform_${dataHash}_${formHash}`;
  }

  // Simple hash function for cache keys
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Get item from cache
  get(key) {
    this.cacheStats.totalGets++;
    
    if (this.cache.has(key)) {
      this.cacheStats.hits++;
      this.accessTimes.set(key, Date.now()); // Update access time for LRU
      
      const item = this.cache.get(key);
      console.log(`🎯 Cache hit for key: ${key.substring(0, 20)}...`);
      
      return item;
    } else {
      this.cacheStats.misses++;
      console.log(`🔍 Cache miss for key: ${key.substring(0, 20)}...`);
      return null;
    }
  }

  // Set item in cache
  set(key, value) {
    this.cacheStats.totalSets++;
    
    // Check if we need to evict items
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }
    
    this.cache.set(key, value);
    this.accessTimes.set(key, Date.now());
    
    console.log(`💾 Cached result for key: ${key.substring(0, 20)}... (cache size: ${this.cache.size})`);
  }

  // Evict least recently used item
  evictLRU() {
    let oldestKey = null;
    let oldestTime = Date.now();
    
    this.accessTimes.forEach((time, key) => {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    });
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.accessTimes.delete(oldestKey);
      this.cacheStats.evictions++;
      
      console.log(`♻️ Evicted LRU item: ${oldestKey.substring(0, 20)}...`);
    }
  }

  // Check if key exists
  has(key) {
    return this.cache.has(key);
  }

  // Delete specific key
  delete(key) {
    const existed = this.cache.has(key);
    this.cache.delete(key);
    this.accessTimes.delete(key);
    
    if (existed) {
      console.log(`🗑️ Deleted cache key: ${key.substring(0, 20)}...`);
    }
    
    return existed;
  }

  // Clear entire cache
  clear() {
    const cacheSize = this.cache.size;
    this.cache.clear();
    this.accessTimes.clear();
    
    console.log(`🧹 Cache cleared (${cacheSize} items removed)`);
    return cacheSize;
  }

  // Get cache size
  size() {
    return this.cache.size;
  }

  // Get cache statistics
  getStats() {
    const hitRate = this.cacheStats.totalGets > 0 ? 
      (this.cacheStats.hits / this.cacheStats.totalGets * 100).toFixed(2) + '%' : '0%';
    
    const missRate = this.cacheStats.totalGets > 0 ? 
      (this.cacheStats.misses / this.cacheStats.totalGets * 100).toFixed(2) + '%' : '0%';
    
    return {
      ...this.cacheStats,
      currentSize: this.cache.size,
      maxSize: this.maxSize,
      hitRate,
      missRate,
      utilizationRate: (this.cache.size / this.maxSize * 100).toFixed(2) + '%',
      averageKeyLength: this.getAverageKeyLength()
    };
  }

  // Get average key length for analysis
  getAverageKeyLength() {
    if (this.cache.size === 0) return 0;
    
    const totalLength = Array.from(this.cache.keys()).reduce((sum, key) => sum + key.length, 0);
    return Math.round(totalLength / this.cache.size);
  }

  // Get cache keys (for debugging)
  getKeys() {
    return Array.from(this.cache.keys());
  }

  // Get cache entries sorted by access time
  getEntriesByAccessTime() {
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      value,
      accessTime: this.accessTimes.get(key),
      accessAge: Date.now() - this.accessTimes.get(key)
    }));
    
    return entries.sort((a, b) => b.accessTime - a.accessTime); // Most recent first
  }

  // Prune cache by age (remove items older than specified time)
  pruneByAge(maxAgeMs = 3600000) { // 1 hour default
    const cutoffTime = Date.now() - maxAgeMs;
    let prunedCount = 0;
    
    this.accessTimes.forEach((time, key) => {
      if (time < cutoffTime) {
        this.cache.delete(key);
        this.accessTimes.delete(key);
        prunedCount++;
      }
    });
    
    console.log(`🧹 Pruned ${prunedCount} aged cache entries (older than ${maxAgeMs}ms)`);
    return prunedCount;
  }

  // Optimize cache by removing duplicate values
  optimizeCache() {
    const valueHashes = new Map();
    const duplicates = [];
    
    this.cache.forEach((value, key) => {
      const valueHash = this.simpleHash(JSON.stringify(value));
      
      if (valueHashes.has(valueHash)) {
        // Found duplicate
        const originalKey = valueHashes.get(valueHash);
        const originalAccess = this.accessTimes.get(originalKey);
        const currentAccess = this.accessTimes.get(key);
        
        // Keep the more recently accessed one
        if (currentAccess > originalAccess) {
          duplicates.push(originalKey);
          valueHashes.set(valueHash, key);
        } else {
          duplicates.push(key);
        }
      } else {
        valueHashes.set(valueHash, key);
      }
    });
    
    // Remove duplicates
    duplicates.forEach(key => {
      this.cache.delete(key);
      this.accessTimes.delete(key);
    });
    
    console.log(`⚡ Cache optimized: removed ${duplicates.length} duplicate entries`);
    return duplicates.length;
  }

  // Set cache configuration
  configure(options = {}) {
    const {
      maxSize = this.maxSize,
      autoOptimize = false,
      maxAge = null
    } = options;
    
    const oldMaxSize = this.maxSize;
    this.maxSize = maxSize;
    
    // If reducing size, evict excess items
    if (maxSize < oldMaxSize && this.cache.size > maxSize) {
      const itemsToEvict = this.cache.size - maxSize;
      for (let i = 0; i < itemsToEvict; i++) {
        this.evictLRU();
      }
    }
    
    if (autoOptimize) {
      this.optimizeCache();
    }
    
    if (maxAge) {
      this.pruneByAge(maxAge);
    }
    
    console.log(`⚙️ Cache configured: maxSize=${maxSize}, autoOptimize=${autoOptimize}, maxAge=${maxAge}`);
  }

  // Preload cache with common transformations
  preload(transformationPairs) {
    console.log(`🚀 Preloading cache with ${transformationPairs.length} transformation pairs...`);
    
    let preloadedCount = 0;
    
    transformationPairs.forEach(({ input, output }) => {
      try {
        const key = this.generateCacheKey(input.cumulativeData, input.auditFormData);
        this.set(key, output);
        preloadedCount++;
      } catch (error) {
        console.warn(`⚠️ Failed to preload transformation pair: ${error.message}`);
      }
    });
    
    console.log(`✅ Preloaded ${preloadedCount} transformation pairs into cache`);
    return preloadedCount;
  }

  // Export cache data
  export() {
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      value,
      accessTime: this.accessTimes.get(key)
    }));
    
    return {
      exportedAt: new Date().toISOString(),
      stats: this.getStats(),
      entries: entries.slice(0, 50), // Limit to first 50 entries for size
      totalEntries: entries.length
    };
  }

  // Import cache data
  import(cacheData) {
    if (!cacheData || !cacheData.entries) {
      throw new Error('Invalid cache data format');
    }
    
    console.log(`📥 Importing ${cacheData.entries.length} cache entries...`);
    
    let importedCount = 0;
    
    cacheData.entries.forEach(({ key, value, accessTime }) => {
      try {
        this.cache.set(key, value);
        this.accessTimes.set(key, accessTime || Date.now());
        importedCount++;
      } catch (error) {
        console.warn(`⚠️ Failed to import cache entry: ${error.message}`);
      }
    });
    
    console.log(`✅ Imported ${importedCount} cache entries`);
    return importedCount;
  }

  // Get cache health report
  getHealthReport() {
    const stats = this.getStats();
    const entries = this.getEntriesByAccessTime();
    
    const health = {
      timestamp: new Date().toISOString(),
      overall: 'healthy',
      issues: [],
      recommendations: []
    };
    
    // Check hit rate
    const hitRate = parseFloat(stats.hitRate);
    if (hitRate < 30) {
      health.issues.push('Low cache hit rate');
      health.recommendations.push('Consider adjusting cache size or reviewing caching strategy');
      health.overall = 'warning';
    }
    
    // Check utilization
    const utilization = parseFloat(stats.utilizationRate);
    if (utilization > 90) {
      health.issues.push('High cache utilization');
      health.recommendations.push('Consider increasing cache size');
      health.overall = 'warning';
    }
    
    // Check for very old entries
    const oldEntriesCount = entries.filter(entry => entry.accessAge > 3600000).length; // 1 hour
    if (oldEntriesCount > this.cache.size * 0.5) {
      health.issues.push('Many old cache entries detected');
      health.recommendations.push('Consider pruning old entries or reducing cache size');
    }
    
    // Check eviction rate
    if (stats.evictions > stats.totalSets * 0.3) {
      health.issues.push('High eviction rate');
      health.recommendations.push('Consider increasing cache size to reduce evictions');
      health.overall = health.overall === 'healthy' ? 'warning' : health.overall;
    }
    
    if (health.issues.length === 0) {
      health.recommendations.push('Cache is operating efficiently');
    }
    
    return {
      ...health,
      stats,
      entryCount: entries.length,
      oldestEntryAge: entries.length > 0 ? entries[entries.length - 1].accessAge : 0,
      newestEntryAge: entries.length > 0 ? entries[0].accessAge : 0
    };
  }

  // Reset cache statistics
  resetStats() {
    this.cacheStats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalSets: 0,
      totalGets: 0
    };
    console.log('📊 Cache statistics reset');
  }

  // Clone cache (create a copy)
  clone() {
    const newCache = new TransformationCache(this.maxSize);
    
    this.cache.forEach((value, key) => {
      newCache.cache.set(key, value);
      newCache.accessTimes.set(key, this.accessTimes.get(key));
    });
    
    newCache.cacheStats = { ...this.cacheStats };
    
    console.log(`📋 Cache cloned with ${this.cache.size} entries`);
    return newCache;
  }

  // Reset cache
  reset() {
    this.clear();
    this.resetStats();
    console.log('🔄 Cache reset complete');
  }
}